export type IubendaPreferences = {
  purposes?: Record<string, boolean>;
  uspr?: {
    s?: boolean;
    sh?: boolean;
    adv?: boolean;
  };
};
export const DEFAULT_PREFERENCES = {
  necessary: false,
  functionality: false,
  experience: false,
  measurement: false,
  marketing: false,
  isReady: false
};
export type IubendaPreferenceState = typeof DEFAULT_PREFERENCES;
export type ConsentValue = "granted" | "denied";
export type GoogleConsentState = Record<"ad_storage" | "ad_user_data" | "ad_personalization" | "analytics_storage" | "functionality_storage" | "personalization_storage" | "security_storage", ConsentValue>;
export const DEFAULT_GOOGLE_CONSENT: GoogleConsentState = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "denied",
  personalization_storage: "denied",
  security_storage: "granted"
};
export type ResolveInput = {
  preferences?: IubendaPreferences;
  notNeeded?: boolean;
  countryCode?: string;
  globalPrivacyControl?: boolean;
  readFailed?: boolean;
};
export const isUS = (countryCode?: string) => /^US(?:-[A-Z]{2})?$/.test(countryCode ?? "");

// Iubenda normally returns "EU", but retain the individual EEA/UK codes too.
const OPT_IN_COUNTRIES = new Set("EU AT BE BG HR CY CZ DK EE FI FR DE GR HU IS IE IT LV LI LT LU MT NL NO PL PT RO SK SI ES SE GB UK CH BR".split(" "));
export function hasKnownRegion(countryCode?: string) {
  return Boolean(countryCode && countryCode !== "XX" && /^(?:[A-Z]{2}|US-[A-Z]{2})$/.test(countryCode));
}
function corePreferences(input: ResolveInput): IubendaPreferenceState {
  if (!hasKnownRegion(input.countryCode)) return {
    ...DEFAULT_PREFERENCES,
    isReady: true
  };
  const purposes = input.preferences?.purposes;
  if (purposes) {
    return {
      necessary: purposes["1"] === true,
      functionality: purposes["2"] === true,
      experience: purposes["3"] === true,
      measurement: purposes["4"] === true,
      marketing: purposes["5"] === true,
      isReady: true
    };
  }
  if (!OPT_IN_COUNTRIES.has(input.countryCode!) && (input.notNeeded || isUS(input.countryCode) && input.preferences?.uspr)) {
    return {
      necessary: true,
      functionality: true,
      experience: true,
      measurement: true,
      marketing: true,
      isReady: true
    };
  }
  return {
    ...DEFAULT_PREFERENCES,
    isReady: true
  };
}
function usRestrictions(input: ResolveInput) {
  const applies = isUS(input.countryCode);
  const gpc = applies && input.globalPrivacyControl === true;
  return {
    sale: applies && (gpc || input.preferences?.uspr?.s === false),
    sharing: applies && (gpc || input.preferences?.uspr?.sh === false || input.preferences?.uspr?.adv === false)
  };
}

/** Native permissions, before the additional application-owned US notice hold. */
export function resolveIubendaPreferences(input: ResolveInput): IubendaPreferenceState {
  const core = corePreferences(input);
  const {
    sale,
    sharing
  } = usRestrictions(input);
  return {
    ...core,
    measurement: core.measurement && !sale,
    marketing: core.marketing && !sale && !sharing
  };
}
export function resolveEffectiveConsent(input: ResolveInput & {
  ready: boolean;
  usAcknowledged: boolean;
}) {
  if (!input.ready || input.readFailed || !hasKnownRegion(input.countryCode)) {
    return {
      preferences: {
        ...DEFAULT_PREFERENCES,
        isReady: input.ready && !input.readFailed
      },
      google: {
        ...DEFAULT_GOOGLE_CONSENT
      }
    };
  }
  const core = corePreferences(input);
  const preferences = resolveIubendaPreferences(input);
  if (isUS(input.countryCode) && !input.usAcknowledged) {
    return {
      preferences: {
        ...DEFAULT_PREFERENCES,
        necessary: true,
        isReady: true
      },
      google: {
        ...DEFAULT_GOOGLE_CONSENT
      }
    };
  }
  if (!input.notNeeded && !Object.keys(input.preferences?.purposes ?? {}).length && !Object.keys(input.preferences?.uspr ?? {}).length) {
    // Readiness can precede the native not-needed/choice callback. Keep the head
    // default rather than ending wait_for_update with a transient security denial.
    return {
      preferences,
      google: {
        ...DEFAULT_GOOGLE_CONSENT
      }
    };
  }
  const {
    sale,
    sharing
  } = usRestrictions(input);
  const signal = (allowed: boolean): ConsentValue => allowed ? "granted" : "denied";
  const google: GoogleConsentState = {
    analytics_storage: signal(core.measurement && !sale),
    ad_storage: signal(core.marketing && !sale && !sharing),
    // Native Iubenda distinguishes sale from sharing/targeted advertising.
    ad_user_data: signal(core.marketing && !sharing),
    ad_personalization: signal(core.marketing && !sharing),
    functionality_storage: signal(core.functionality),
    personalization_storage: signal(core.experience),
    security_storage: signal(core.functionality)
  };
  return {
    preferences,
    google
  };
}